/**
 * Test Helper for ConsentGuard
 * Use this in browser console to test different consent states
 *
 * USAGE IN BROWSER CONSOLE:
 * 1. Sign in to the app
 * 2. Open DevTools Console
 * 3. Run: window.testConsentGuard.simulateNoConsent()
 * 4. Navigate to home page - should redirect to /consent/email
 * 5. Run: window.testConsentGuard.simulateWithConsent()
 * 6. Navigate to home page - should work normally
 */
import { sessionManager } from '../services/user/SessionManager';
import type { UserProfile } from '../types/user';
export const testConsentGuard = {
    /**
     * Simulate a user without consent
     * Removes consent fields from the profile
     */
    simulateNoConsent() {
        const state = sessionManager.getSessionState();
        if (!state.profile) {
            console.error('No profile found. Please sign in first.');
            return;
        }
        // Create a modified profile without consent fields
        const { emailMarketingConsent, emailMarketingConsentAt, emailMarketingConsentVersion, ...profileWithoutConsent } = state.profile;
        const modifiedProfile: UserProfile = profileWithoutConsent;
        // Override the session state (this is a hack for testing)
        (sessionManager as any).sessionState.profile = modifiedProfile;
        console.log('Current profile:', modifiedProfile);
    },
    /**
     * Simulate a user with valid consent
     * Adds consent fields to the profile
     */
    simulateWithConsent() {
        const state = sessionManager.getSessionState();
        if (!state.profile) {
            console.error('No profile found. Please sign in first.');
            return;
        }
        // Create a modified profile with consent fields
        const modifiedProfile: UserProfile = {
            ...state.profile,
            emailMarketingConsent: true,
            emailMarketingConsentAt: new Date().toISOString(),
            emailMarketingConsentVersion: 'v1',
        };
        // Override the session state (this is a hack for testing)
        (sessionManager as any).sessionState.profile = modifiedProfile;
        console.log('Current profile:', modifiedProfile);
    },
    /**
     * Simulate a user with outdated consent version
     * Sets consent version to v0 (outdated)
     */
    simulateOutdatedConsent() {
        const state = sessionManager.getSessionState();
        if (!state.profile) {
            console.error('No profile found. Please sign in first.');
            return;
        }
        // Create a modified profile with outdated consent version
        const modifiedProfile: UserProfile = {
            ...state.profile,
            emailMarketingConsent: true,
            emailMarketingConsentAt: new Date().toISOString(),
            emailMarketingConsentVersion: 'v0', // Outdated version
        };
        // Override the session state (this is a hack for testing)
        (sessionManager as any).sessionState.profile = modifiedProfile;
        console.log('Current profile:', modifiedProfile);
    },
    /**
     * Reset to actual profile from backend
     */
    async reset() {
        try {
            await sessionManager.refreshSession();
        }
        catch (error) {
            console.error('Failed to reset profile:', error);
        }
    },
    /**
     * Show current profile state
     */
    showProfile() {
        const profile = sessionManager.getProfile();
        console.log('Current profile:', profile);
        console.log('Consent fields:', {
            emailMarketingConsent: profile?.emailMarketingConsent,
            emailMarketingConsentAt: profile?.emailMarketingConsentAt,
            emailMarketingConsentVersion: profile?.emailMarketingConsentVersion,
        });
    },
};
// Expose to window for browser console access
if (typeof window !== 'undefined') {
    (window as any).testConsentGuard = testConsentGuard;
}
